import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { formatDuration } from '@/hooks/useDeepWorkMetrics';
import styles from '@/pages/WeeklyPlanningPage.module.css';

export interface WeeklyHeaderProps {
  weekStart: Date;
  weekEnd: Date;
  onBack: () => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  isCurrentWeek: boolean;
  totalDeepWorkMinutes?: number;
}

export function WeeklyHeader({
  weekStart,
  weekEnd,
  onBack,
  onPrevWeek,
  onNextWeek,
  onToday,
  isCurrentWeek,
  totalDeepWorkMinutes = 0,
}: WeeklyHeaderProps) {
  const weekRange = `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
  const weekNumber = format(weekStart, "'W'ww");

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <motion.button
          className={styles.backBtn}
          onClick={onBack}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-label="Back to daily view"
        >
          <span aria-hidden="true">&larr;</span>
          <span>Back</span>
        </motion.button>
      </div>

      <div className={styles.headerCenter}>
        <div className={styles.weekNav}>
          <motion.button
            className={styles.navArrow}
            onClick={onPrevWeek}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Previous week"
          >
            &lsaquo;
          </motion.button>

          <div className={styles.weekInfo}>
            <motion.h1
              className={styles.weekTitle}
              key={weekRange}
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {weekRange}
            </motion.h1>
            <span className={styles.weekNumber}>{weekNumber}</span>
          </div>

          <motion.button
            className={styles.navArrow}
            onClick={onNextWeek}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Next week"
          >
            &rsaquo;
          </motion.button>
        </div>

        {!isCurrentWeek && (
          <motion.button
            className={styles.todayBtn}
            onClick={onToday}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            This Week
          </motion.button>
        )}
      </div>

      <div className={styles.headerRight}>
        {/* Deep work metrics preview */}
        <div className={styles.metricsPreview}>
          <span className={styles.metricsIcon} aria-hidden="true">
            &#128200;
          </span>
          <span className={styles.metricsText}>
            {formatDuration(totalDeepWorkMinutes)} deep work
          </span>
        </div>
      </div>
    </header>
  );
}
